import {
  Button,
  ButtonProps,
  Link as ChakraLink,
  LinkProps as ChakraLinkProps,
  ComponentWithAs,
  LinkOverlay,
  forwardRef
} from "@chakra-ui/react"
import path from "path-browserify"
import { useCallback } from "react"
import {
  LinkProps as RouterLinkProps,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom"

// For navigating absolutely to the root of the url.
export type LinkTo = RouterLinkProps["to"]
// For navigating relative to the current url path.
// Since we use path.join, we can combine paths with special patterns, like "../",
// which would go back one level in the current pathname.
export type LinkRelativeTo = string
export type LinkHref = ChakraLinkProps["href"]

export type LinkProps = (ButtonProps &
  Omit<ChakraLinkProps, "to" | "href"> &
  Omit<RouterLinkProps, "to" | "href">) &
  (
    | ({ to?: LinkTo } | { relativeTo?: LinkRelativeTo })
    | { href?: ChakraLinkProps["href"] }
  ) & {
    underlineBehavior?: "always" | "hover" | "never"
  }

// Properly handle the "to" prop for navigation.
export function useHandleTo() {
  const location = useLocation()
  const history = useHistory()

  const handleTo = useCallback(
    (to: LinkTo) => {
      if (typeof to === "function") {
        history.push(to(location))
      } else {
        history.push(to)
      }
    },
    [location, history]
  )

  return handleTo
}

const Link: ComponentWithAs<"a", LinkProps> = forwardRef<LinkProps, "a">(
  ({ as, onClick, underlineBehavior = "never", ...props }, ref) => {
    const { url } = useRouteMatch()
    const handleTo = useHandleTo()

    // Conditionally apply relativeTo or to based on the props.
    let to: LinkTo | null = null

    if ("to" in props) {
      if (props.to) to = props.to
      // Delete existing to prop to override.
      delete props.to
    }

    if ("relativeTo" in props) {
      if (props.relativeTo) to = path.join(url, props.relativeTo)
      // Delete existing relativeTo prop to not pass to link component.
      delete props.relativeTo
    }

    let href: LinkHref | null = null

    // Provide an href by default (to support default behaviors, opening links
    // in a new tab using Meta + Click.
    if (typeof to === "string" && (!("href" in props) || !props.href)) {
      if (url.startsWith(to) || to.startsWith(url)) {
        // Derive the href prop based on whether the to prop is used in the current
        // URL's query params. If so, we update that query param with the to value.
        // Otherwise, we simply set the href to the to value.
        const currentUrl = new URL(window.location.href)
        const matchingQueryParamEntry = Array.from(
          currentUrl.searchParams
        ).find(([key, value]) => value === url)

        // If we've found a matching query param entry, we can derive the href using it.
        if (matchingQueryParamEntry) {
          const [queryParam] = matchingQueryParamEntry
          currentUrl.searchParams.set(queryParam, to)
          href = currentUrl.href
        }
      }

      // Fallback to simply using the to value.
      href ||= to

      if ("href" in props) {
        // Delete existing href prop to override.
        delete props.href
      }
    }

    // Custom click handler to support more versatile route handling than what is provided by
    // react router's Link component which doesn't support an href prop (the href of the anchor
    // element is set to the to prop and cannot be overridden, which means we can't support user
    // clicking with modified events (Ctrl+click, Cmd+click, etc.).
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        // Call any provided onClick handler first
        onClick?.(event as React.MouseEvent<HTMLAnchorElement>)

        // Check for modified events (Ctrl+click, Cmd+click, etc.)
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.button !== 0
        ) {
          // Let the browser handle it naturally with the href
          return
        }

        // Check if we have a 'to' prop for React Router navigation
        if (to && !event.defaultPrevented) {
          event.preventDefault()
          handleTo(to)
        }

        // If no 'to' prop but we have href, let browser handle it naturally
      },
      [to, handleTo, onClick]
    )

    // Most often we wish to remove the underline from links as they are often
    // used as buttons. On top of that, it seems specifically tricky to do so,
    // as using _hover prop overrides other desired hover styles.
    const underlineProps =
      underlineBehavior === "always"
        ? { textDecor: "underline" }
        : underlineBehavior === "hover"
        ? {
            textDecor: "none",
            sx: { ["&:hover"]: { textDecor: "underline" } }
          }
        : {
            textDecor: "none",
            sx: { ["&:hover"]: { textDecor: "none" } }
          }

    // Conditionally set position of the link to static when using LinkOverlay
    // to ensure the link actually covers the intended area.
    const posProps: ButtonProps = as === LinkOverlay ? { pos: "static" } : {}

    return (
      <Button
        ref={ref}
        as={as ?? (to ? "a" : ChakraLink)}
        className="Link"
        target={"href" in props ? "_blank" : undefined}
        variant="link"
        to={to}
        href={href}
        onClick={to ? handleClick : onClick}
        {...underlineProps}
        {...posProps}
        {...props}
      />
    )
  }
)

Link.displayName = "Link"

export default Link
